import { useCallback, useMemo, useState, ReactElement } from 'react';
import * as Yup from 'yup';
import valid from 'card-validator';
import InputMask from 'react-input-mask';
import { Button, Box, ButtonBase, FormControl, Grid, Stack } from '@mui/material';
import moment from 'moment';
import { PaymentGateways } from '../../../../../../types';
import CreditCard from '@mui/icons-material/CreditCard';
import usePayButtonText from '../../../../hooks/usePayButtonText';
import CBModal from '../../../../../../ui-component/cb-modal/CBModal';
import { useMediaQuery } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import WidgetTextField from '../../../../../../ui-component/form/WidgetTextField';
import CvcIcon from '../../../../../../ui-component/form/card-icons/cvc-icon';
import VisaIcon from '../../../../../../ui-component/form/card-icons/visa-icon';
import MastercardIcon from '../../../../../../ui-component/form/card-icons/mastercard-icon';
import AmexIcon from '../../../../../../ui-component/form/card-icons/amex-icon';
import DinersIcon from '../../../../../../ui-component/form/card-icons/diners-icon';
import useExtendedFormik from '../../../../../../hooks/useExtendedFormik';

interface AuthorizeNetFormProps {
    submitBooking: (details?: object) => void;
    amount?: number;
    fee?: string | number;
    ToggleBtn?: ReactElement;
    disabled?: boolean;
}

const AuthorizeNetForm = ({ submitBooking, amount, ToggleBtn, disabled, fee = 0 }: AuthorizeNetFormProps) => {
    const matchSm = useMediaQuery((themeParam: Theme) => themeParam.breakpoints.down('sm'));
    const [showModal, setShowModal] = useState<boolean>(false);

    const { payButtonTitle } = usePayButtonText({
        predefinedAmount: amount,
        fee
    });

    const { handleSubmit, values, touched, errors, handleBlur, handleChange, setFieldTouched, setFieldValue, resetForm } =
        useExtendedFormik({
            validateOnChange: true,
            initialValues: {
                cardHolderName: '',
                cardNumber: '',
                expDate: '',
                cardCode: '',
                address: {
                    postal_code: ''
                }
            },
            validationSchema: Yup.object().shape({
                cardHolderName: Yup.string().required('Card holder name is required'),
                cardNumber: Yup.string()
                    .test('test-number', 'Credit card number is invalid', (value) => valid.number(value).isValid)
                    .required(),
                expDate: Yup.string()
                    .required('Expiry date is required')
                    .test('test-valid-exp-date', 'Expiry date is invalid', (value) => {
                        const expDate = moment(value, 'MM/YY');
                        return expDate.isValid();
                    })
                    .test('test-exp-date', 'Expiry date cannot be in the past', (value) => {
                        const expDate = moment(value, 'MM/YY');
                        return expDate.isSameOrAfter(moment().format(), 'month');
                    }),
                cardCode: Yup.string()
                    .required('CVV is required')
                    .matches(/^[0-9]+$/, 'Must be only digits')
                    .min(3, 'Must be at least 3 digits'),
                address: Yup.object().shape({
                    postal_code: Yup.string().required('Postal code is required')
                })
            }),
            onSubmit: (formData) => {
                const expDate = formData.expDate.split('/');
                submitBooking({
                    ...formData,
                    gateway: PaymentGateways.AuthorizeNet,
                    month: expDate[0],
                    year: expDate[1]
                });
            }
        });

    const openModal = useCallback(() => {
        setShowModal(true);
    }, [setShowModal]);

    const closeModal = useCallback(() => {
        setShowModal(false);
        resetForm();
    }, [setShowModal, resetForm]);

    // change card number input mask in case of entering of Amex card number (starting with 34 or 37)
    const creditCardMask = useMemo(
        () => (/^3[47]/.test(values.cardNumber) ? '9999 9999 9999 999' : '9999 9999 9999 9999'),
        [values.cardNumber]
    );

    return (
        <>
            {ToggleBtn ? (
                <ButtonBase sx={{ mb: 1.5, width: '100%' }} onClick={openModal} disabled={disabled}>
                    {ToggleBtn}
                </ButtonBase>
            ) : (
                <Button
                    onClick={openModal}
                    fullWidth
                    variant="contained"
                    color="primary"
                    sx={{ height: '45px', mb: 1.5 }}
                    startIcon={<CreditCard />}
                    disabled={disabled}
                >
                    Credit Card
                </Button>
            )}

            <CBModal
                title={<Box px={3}>Payment Details</Box>}
                open={showModal}
                onClose={closeModal}
                fullWidth
                maxWidth="sm"
                okButtonText={payButtonTitle()}
                onClickOk={handleSubmit}
                fullScreen={matchSm}
                id="authorize-modal"
                overrideColors={{ contentBgColor: '#eee', footerBgColor: '#fff' }}
            >
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={1.5} rowSpacing={0.5} justifyContent="center">
                        <Grid item xs={12}>
                            <FormControl fullWidth error={Boolean(touched.cardNumber && errors.cardNumber)}>
                                <InputMask
                                    // Fixing credit card autocomplete bug
                                    beforeMaskedStateChange={(states) => {
                                        const { previousState, currentState, nextState } = states;

                                        if (
                                            !nextState?.value.replace(/\s|_/gi, '') &&
                                            currentState?.value &&
                                            nextState?.value === previousState?.value
                                        ) {
                                            return { ...nextState, value: currentState.value.replace(/(\d{4})/gi, '$1 ') };
                                        }

                                        if (
                                            currentState &&
                                            previousState &&
                                            nextState &&
                                            // @ts-ignore - For some reason 'enteredString' not in state type
                                            !nextState.enteredString &&
                                            currentState.value === previousState.value?.replaceAll(' ', '')
                                        ) {
                                            return previousState;
                                        }

                                        return nextState;
                                    }}
                                    mask={creditCardMask}
                                    value={values.cardNumber}
                                    onChange={(event) => {
                                        setFieldValue('cardNumber', event.target.value.replace('_', ''));
                                        setFieldTouched('cardNumber', false);
                                    }}
                                    onBlur={handleBlur}
                                >
                                    <WidgetTextField
                                        fullWidth
                                        id="cardNumber"
                                        name="cardNumber"
                                        label="Card Number"
                                        error={Boolean(touched.cardNumber && errors.cardNumber)}
                                        helperText={touched.cardNumber && errors.cardNumber}
                                        InputProps={{
                                            endAdornment: (
                                                <Stack direction="row" spacing={0.5}>
                                                    <VisaIcon />
                                                    <MastercardIcon />
                                                    <AmexIcon />
                                                    <DinersIcon />
                                                </Stack>
                                            )
                                        }}
                                    />
                                </InputMask>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                            <InputMask
                                mask="99/99"
                                value={values.expDate}
                                maskPlaceholder="MM/YY"
                                onChange={(event) => {
                                    setFieldValue('expDate', event.target.value);
                                    setFieldTouched('expDate', false);
                                }}
                                onBlur={handleBlur}
                            >
                                <WidgetTextField
                                    fullWidth
                                    id="exp-date"
                                    name="expDate"
                                    label="Card Expiry Date"
                                    error={Boolean(touched?.expDate && errors?.expDate)}
                                    helperText={touched?.expDate && errors?.expDate}
                                />
                            </InputMask>
                        </Grid>

                        <Grid item xs={6}>
                            <FormControl fullWidth error={Boolean(touched.cardCode && errors.cardCode)}>
                                <WidgetTextField
                                    fullWidth
                                    id="cardCode"
                                    name="cardCode"
                                    label="CVV Code"
                                    value={values.cardCode}
                                    onBlur={handleBlur}
                                    onChange={(event) => {
                                        setFieldValue('cardCode', event.target.value.replace(/\D/g, '').slice(0, 4));
                                    }}
                                    error={Boolean(touched.cardCode && errors.cardCode)}
                                    helperText={touched.cardCode && errors.cardCode}
                                    InputProps={{ endAdornment: <CvcIcon /> }}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <WidgetTextField
                                id="card-holder-name"
                                name="cardHolderName"
                                label="Card Holder Name"
                                value={values.cardHolderName}
                                onChange={handleChange}
                                error={Boolean(touched?.cardHolderName && errors?.cardHolderName)}
                                helperText={touched?.cardHolderName && errors?.cardHolderName}
                                onBlur={handleBlur}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <WidgetTextField
                                id="address-postal-code"
                                name="address.postal_code"
                                label="Postal Code"
                                value={values.address?.postal_code}
                                onChange={handleChange}
                                error={Boolean(touched.address?.postal_code && errors.address?.postal_code)}
                                helperText={touched.address?.postal_code && errors.address?.postal_code}
                                onBlur={handleBlur}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </form>
            </CBModal>
        </>
    );
};

export default AuthorizeNetForm;
